<template>
    <teleport to="#sl-order-tools__reset-back-surface-button">
        <tippy placement="left">
            <div class="sl-order-tools__button" @click="resetBackSurface">
                <mdi-icon icon="mdiUndo" />
            </div>

            <template #content>
                {{ $t('parameters.RESET_BACK_SURFACE_BUTTON.button_label') }}

                <template v-if="tooltipParenthesis.length">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    (<span v-html="tooltipParenthesis.join(', ')"></span>)
                </template>
            </template>
        </tippy>
    </teleport>
</template>

<script>
import Parameter from '@/models/Parameter';
import AbstractField from '../AbstractField.vue';
import useFittingComposable from '@/composables/FittingComposable';

/**
 * All the parameters in these sections are reset upon clicking the button.
 *
 * @type {string[]}
 */
const SECTION_CODES = [
    'BACK_OPTICAL_ZONE',
    'REVERSE_ZONE',
    'ALIGNMENT_ZONE',
    'LIMBAL_ZONE',
    'SCLERAL_ZONE',
    'PERIPHERAL_ZONE',
    'MERIDIAN_1',
    'MERIDIAN_2',
    'MERIDIAN_3',
    'MERIDIAN_4',

    // Liberty
    'OPTICAL_FLAT',
    'OPTICAL_STEEP',
    'OPTICAL_FLAT2',
    'OPTICAL_STEEP2',
    'ALIGNMENT_FLAT',
    'ALIGNMENT_STEEP',
    'ALIGNMENT_FLAT2',
    'ALIGNMENT_STEEP2',
    'REVERSE_FLAT',
    'REVERSE_STEEP',
    'REVERSE_FLAT2',
    'REVERSE_STEEP2',
];

/**
 * All these parameters are reset upon clicking the button.
 *
 * @type {string[]}
 */
const PARAMETER_CODES = [
    'LENS_EDGE_RADIUS',
];

export default
{
    name: 'ResetBackSurfaceButton',

    extends: AbstractField,

    setup()
    {
        const { getLensLetter } = useFittingComposable();

        return {
            getLensLetter,
        };
    },

    computed:
    {
        fittings()
        {
            return this.$store.state.lensOrder.fittings;
        },

        /**
         * The ID of the current fitting.
         *
         * @returns {number}
         */
        cFittingId()
        {
            return this.fittings.current[this.side];
        },

        tooltipParenthesis()
        {
            const parts = [];

            // Side
            const side = this.$t(`common.${this.side}`);
            parts.push(side);

            // Fitting letter
            if(this.fittings[this.side].length > 2)
            {
                parts.push(this.$t('common.lens') + ' ' +
                    this.getLensLetter(this.cFittingId)
                );
            }

            return parts;
        },

        /**
         * The visible parameters that must be reset when clicking the button.
         *
         * @returns {Parameter[]}
         */
        visibleBackSurfaceParameters()
        {
            /** @type {Parameter[]} */
            const params = this.$store.getters['lensOrder/getPrototype'](this.side).getParametersAsArray();

            return params.filter(p =>
            {
                // Exclude invisible parameters
                if(
                    !p.is_enabled
                    || p.form_display === 'HIDDEN'
                    || p.form_splitted_display === 'HIDDEN'
                )
                {
                    return false;
                }

                // Include select parameters
                if(PARAMETER_CODES.includes(p.code))
                {
                    return true;
                }

                // Include parameters in select sections
                if(SECTION_CODES.includes(p.section))
                {
                    return true;
                }

                return false;
            });
        },
    },

    methods:
    {
        resetBackSurface()
        {
            for(const param of this.visibleBackSurfaceParameters)
            {
                param.setValue(param.getDefaultValue());
            }

            this.$store.dispatch('lensOrder/templateAndValidate', { side: this.side });
        },
    },
};
</script>

<style lang="scss" scoped>
</style>
